import { computed, ref, unref, watchEffect, type Ref } from 'vue'
import type { BulkDiscountType } from '~/shared/bulk/discount.js'

export interface BulkAccessStatus {
  success: boolean
  found?: boolean
  code?: string
  discountType: BulkDiscountType
  discountActive: boolean
  /** Pre-rendered "15% off" / "$40.00 each"; null when no discount is set. */
  discountLabel: string | null
  percentOff: number | null
  /** Per-membership price in cents; only set when discountType is 'fixed'. */
  fixedPrice: number | null
  discountCap: number | null
  discountUsed: number
  remaining: number | null
  usedPct: number | null
  createdAt: unknown | null
  status: string
}

export interface UseBulkDiscountStatus {
  status: Ref<BulkAccessStatus | null>
  loading: Ref<boolean>
  error: Ref<string | null>
  refresh: () => Promise<void>
  found: Ref<boolean>
  hasCap: Ref<boolean>
  remaining: Ref<number | null>
  usedPct: Ref<number | null>
  discountActive: Ref<boolean>
  discountLabel: Ref<string | null>
}

export function useBulkDiscountStatus(subkreweId: Ref<string | null | undefined> | string | null | undefined): UseBulkDiscountStatus {
  const status = ref<BulkAccessStatus | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)
  // No client/store/Firestore fallback: API is the single source of truth
  let requestId = 0

  const refresh = async () => {
    const sid = typeof subkreweId === 'object' ? unref(subkreweId) : subkreweId
    if (!sid) {
      status.value = null
      return
    }
    const currentRequestId = ++requestId
    loading.value = true
    error.value = null
    try {
      const res = await $fetch<BulkAccessStatus & { success: boolean, found?: boolean, quantityAvailable?: number }>(`/api/bulk/access-code?subkreweId=${encodeURIComponent(sid)}`, {
        // Suppress error logging for 401s
        onResponseError({ response }) {
          if (response.status === 401) {
            // Silently handle auth errors
            return
          }
        },
      })
      // Discard stale response if a newer request has started
      if (currentRequestId !== requestId) return
      if (res?.success && res.found) {
        const quantityAvailable: number | null = typeof res.quantityAvailable === 'number' ? res.quantityAvailable : null
        status.value = {
          success: true,
          found: true,
          code: res.code,
          discountType: res.discountType === 'fixed' ? 'fixed' : 'percent',
          discountActive: !!res.discountActive,
          discountLabel: res.discountLabel ?? null,
          percentOff: res.percentOff ?? null,
          fixedPrice: res.fixedPrice ?? null,
          discountCap: res.discountCap ?? quantityAvailable ?? null,
          discountUsed: typeof res.discountUsed === 'number' ? res.discountUsed : 0,
          remaining: typeof res.remaining === 'number' ? res.remaining : null,
          usedPct: typeof res.usedPct === 'number' ? res.usedPct : null,
          createdAt: res.createdAt ?? null,
          status: res.status ?? 'created',
        }
      }
      else if (res?.success && res.found === false) {
        // Rely solely on API-provided configured values for future code creation
        const percentOff = typeof res.percentOff === 'number' ? res.percentOff : null
        const discountCap = typeof res.discountCap === 'number' ? res.discountCap : null
        status.value = {
          success: true,
          found: false,
          code: undefined,
          discountType: res.discountType === 'fixed' ? 'fixed' : 'percent',
          discountActive: !!res.discountActive,
          discountLabel: res.discountLabel ?? null,
          percentOff,
          fixedPrice: typeof res.fixedPrice === 'number' ? res.fixedPrice : null,
          discountCap,
          discountUsed: 0,
          remaining: typeof discountCap === 'number' ? discountCap : null,
          usedPct: null,
          createdAt: null,
          status: 'not-found',
        }
      }
      else {
        throw new Error((res as unknown as { message?: string })?.message || 'Failed to load')
      }
    }
    catch (e: unknown) {
      if (currentRequestId !== requestId) return
      // Handle authentication errors silently - user may not be authorized
      if (e && typeof e === 'object' && 'statusCode' in e && e.statusCode === 401) {
        error.value = null // Don't set error for auth issues
        status.value = null
      }
      else {
        error.value = e instanceof Error ? e.message : 'Failed to load'
      }
    }
    finally {
      loading.value = false
    }
  }

  // Auto-refresh when subkreweId becomes available
  watchEffect(() => {
    const sid = typeof subkreweId === 'object' ? unref(subkreweId) : subkreweId
    if (sid) {
      // Fire and forget; tests can await refresh() explicitly
      refresh()
    }
  })

  const found = computed(() => !!status.value?.found)
  const hasCap = computed(() => typeof status.value?.discountCap === 'number' && (status.value?.discountCap as number) > 0)
  const remaining = computed(() => status.value?.remaining ?? null)
  const usedPct = computed(() => status.value?.usedPct ?? null)
  const discountActive = computed(() => !!status.value?.discountActive)
  const discountLabel = computed(() => status.value?.discountLabel ?? null)

  return { status, loading, error, refresh, found, hasCap, remaining, usedPct, discountActive, discountLabel }
}
